import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  useTheme,
  alpha,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  School as SchoolIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import apiService from "../../services/apiService";
import toast from "react-hot-toast";

const MdlSelectMultipleSchool = ({
  open,
  onClose,
  selectedSchools = [],
  onSelectChange,
  onFinancialYearChange,
  onMonthChange,
  title = "Select Schools",
}) => {
  const theme = useTheme();
  const [schools, setSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [financialYears, setFinancialYears] = useState([]);
  const [loadingFinancialYears, setLoadingFinancialYears] = useState(false);
  // Store selected schools as objects with full data
  const [internalSelected, setInternalSelected] = useState([]);
  // financial_year_id expected by API (year number) - initialized as null, set from API
  const [selectedFinancialYearId, setSelectedFinancialYearId] = useState(null);
  const [financialYearAnchorEl, setFinancialYearAnchorEl] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null); // 1-12 or null
  const [selectedMonthValue, setSelectedMonthValue] = useState(null); // "YYYY-MM" or null

  // Sync internal state with prop changes and match with loaded schools
  useEffect(() => {
    if (!Array.isArray(selectedSchools) || selectedSchools.length === 0) {
      setInternalSelected([]);
      return;
    }

    // If selectedSchools contains objects, use them directly
    if (typeof selectedSchools[0] === 'object') {
      setInternalSelected(selectedSchools);
      return;
    }

    // If selectedSchools contains IDs and schools are loaded, match them
    if (schools.length > 0) {
      const matchedSchools = schools.filter((school) => {
        const schoolId = school.school_id || school.id;
        return selectedSchools.includes(schoolId);
      });
      setInternalSelected(matchedSchools.length > 0 ? matchedSchools : []);
    } else {
      setInternalSelected([]);
    }
  }, [selectedSchools, schools]);

  // Base calendar year for selected financial year (used only for month/year display & YYYY-MM)
  const baseYear = useMemo(() => {
    if (!selectedFinancialYearId) return new Date().getFullYear();
    
    // Find the financial year object to extract numeric year
    const fy = financialYears.find((item) => {
      const id = item.financial_year_id || item.year_id || item.id || item.year;
      return String(id) === String(selectedFinancialYearId);
    });
    
    // Try to get numeric year from the object
    if (fy) {
      const year = fy.year || fy.start_year || fy.calendar_year;
      if (year && !isNaN(Number(year))) return Number(year);
      
      // Try parsing from financial_year_name (e.g., "2026-2027" -> 2026)
      if (fy.financial_year_name) {
        const match = fy.financial_year_name.match(/\d{4}/);
        if (match) return Number(match[0]);
      }
    }
    
    // Fallback: try to convert ID to number (works if ID is numeric)
    const numericId = Number(selectedFinancialYearId);
    return !isNaN(numericId) ? numericId : new Date().getFullYear();
  }, [selectedFinancialYearId, financialYears]);

  // API: Load schools for selected financial year + month
  const loadSchools = useCallback(async () => {
    // Require both financial year and month to be selected before calling API
    if (!selectedFinancialYearId || !selectedMonth) return;

    setLoadingSchools(true);
    try {
      // Format selected month as year-month (e.g., "2026-04")
      // April (4) to December (12) belong to baseYear
      // January (1) to March (3) belong to baseYear + 1
      const formattedMonth = `${selectedMonth >= 4 ? baseYear : baseYear + 1}-${String(selectedMonth).padStart(2, '0')}`;

      // Use getMySchools API with financial year and selected month
      const result = await apiService.getMySchools(
        selectedFinancialYearId, // financial_year_id (year number)
        formattedMonth, // selected_month (year-month format, e.g., "2026-04")
        100, // limit
        0, // offset
        'ASC' // order
      );

      if (result.success) {
        const schoolsData = result.data || [];
        setSchools(schoolsData);
      } else {
        console.error("[MdlSelectMultipleSchool] API Error:", result.error);
        toast.error(result.error || "Failed to load schools");
        setSchools([]);
      }
    } catch (error) {
      toast.error("Error loading schools: " + error.message);
      setSchools([]);
    } finally {
      setLoadingSchools(false);
    }
  }, [selectedFinancialYearId, selectedMonth, baseYear]);

  // Load schools when dialog opens, financial year changes, or month changes
  useEffect(() => {
    if (open && selectedFinancialYearId !== null) {
      loadSchools();
    }
  }, [open, selectedFinancialYearId, loadSchools]);

  // Create a Map for O(1) school lookup by ID
  const schoolsMap = useMemo(() => {
    const map = new Map();
    schools.forEach((school) => {
      const id = school.school_id || school.id;
      if (id) {
        map.set(id, school);
      }
    });
    return map;
  }, [schools]);

  // Create a Set of selected IDs for O(1) lookup instead of O(n) some() calls
  const selectedIdsSet = useMemo(() => {
    const set = new Set();
    internalSelected.forEach((s) => {
      const id = s.school_id || s.id;
      if (id) set.add(id);
    });
    return set;
  }, [internalSelected]);

  // Event Handlers - Optimized with Map lookup
  const handleSelectSchool = useCallback(
    (schoolId) => {
      setInternalSelected((prev) => {
        // Use Map for O(1) lookup instead of O(n) find
        const isSelected = prev.some((s) => {
          const id = s.school_id || s.id;
          return id === schoolId;
        });
        
        if (isSelected) {
          // Remove from selection - O(n) filter but necessary
          return prev.filter((s) => {
            const id = s.school_id || s.id;
            return id !== schoolId;
          });
        } else {
          // Add to selection - O(1) Map lookup instead of O(n) find
          const school = schoolsMap.get(schoolId);
          
          if (school) {
            // Store only the required fields: school_code, school_name, admin_number, admin_name
            const schoolData = {
              school_id: school.school_id || school.id,
              id: school.school_id || school.id,
              school_code: school.school_code || school.schoolCode || '',
              school_name: school.school_name || school.name || '',
              admin_number: school.admin_number || school.adminNumber || school.admin_phone || school.phone || '',
              admin_name: school.admin_name || school.contactPerson || '',
            };
            return [...prev, schoolData];
          }
          return prev;
        }
      });
    },
    [schoolsMap]
  );

  const handleClearAll = useCallback(() => {
    setInternalSelected([]);
  }, []);

  const handleDone = useCallback(() => {
    if (onSelectChange) {
      // Extract IDs from selected school objects
      const selectedIds = internalSelected.map((s) => s.school_id || s.id);
      // Pass both IDs and full school data
      onSelectChange(selectedIds, internalSelected);
    }
    if (onFinancialYearChange) {
      onFinancialYearChange(selectedFinancialYearId);
    }
    if (onMonthChange) {
      onMonthChange(selectedMonthValue);
    }
    onClose();
  }, [
    internalSelected,
    onSelectChange,
    onFinancialYearChange,
    onMonthChange,
    selectedFinancialYearId,
    selectedMonthValue,
    onClose,
  ]);

  const handleClose = useCallback(() => {
    // Reset to original selection on cancel
    if (Array.isArray(selectedSchools) && selectedSchools.length > 0) {
      if (typeof selectedSchools[0] === 'object') {
        setInternalSelected(selectedSchools);
      } else {
        // It's an array of IDs, reset to empty and let the sync effect handle it
        setInternalSelected([]);
      }
    } else {
      setInternalSelected([]);
    }
    onClose();
  }, [selectedSchools, onClose]);

  // API: Load financial year list
  const loadFinancialYears = useCallback(async () => {
    setLoadingFinancialYears(true);
    try {
      const result = await apiService.getFinancialYearList();
      if (result.success) {
        const list = result.data || [];
        setFinancialYears(list);

        // On first load (no year selected yet), prefer the year whose status is "Current"; fallback to first item
        if (list.length > 0 && !selectedFinancialYearId) {
          // Try to find a row that is marked as current
          const currentRow =
            list.find((fy) => {
              const status =
                (fy.status ||
                  fy.financial_year_status ||
                  fy.current_status ||
                  fy.is_current ||
                  fy.isCurrent) ?? "";

              // Backend marks current year explicitly as "Current"
              if (typeof status === "string" && status === "Current") {
                return true;
              }

              return false;
            }) || list[0];

          const first = currentRow;
          const id =
            first.financial_year_id ||
            first.year_id ||
            first.id ||
            first.year ||
            new Date().getFullYear();
          // Store the actual ID (could be string like "FYR0011" or number) - use as-is for API
          setSelectedFinancialYearId(id);
          if (onFinancialYearChange) onFinancialYearChange(id);
        }
      } else {
        console.error("[MdlSelectMultipleSchool] getFinancialYearList API Error:", result.error);
        toast.error(result.error || "Failed to load financial years");
      }
    } catch (error) {
      console.error("[MdlSelectMultipleSchool] getFinancialYearList Exception:", error);
      toast.error("Error loading financial years: " + error.message);
    } finally {
      setLoadingFinancialYears(false);
    }
  }, [onFinancialYearChange]);

  useEffect(() => {
    if (open) {
      loadFinancialYears();
    }
  }, [open, loadFinancialYears]);

  // Financial Year Handlers
  const handleFinancialYearClick = (event) => {
    setFinancialYearAnchorEl(event.currentTarget);
  };

  const handleFinancialYearClose = () => {
    setFinancialYearAnchorEl(null);
  };

  const handleFinancialYearSelect = (year) => {
    // Store the actual ID (could be string or number) - use as-is for API
    setSelectedFinancialYearId(year);
    if (onFinancialYearChange) {
      onFinancialYearChange(year);
    }
    handleFinancialYearClose();
  };

  // Get month names (short format for compact display)
  const getMonthName = (monthNumber) => {
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];
    return months[monthNumber - 1];
  };

  // Get financial year months (April to March)
  const getFinancialYearMonths = useMemo(() => {
    const months = [];
    // Financial year starts from April (month 4) to March (month 3)
    // April to December (months 4-12) belong to selected year
    for (let i = 4; i <= 12; i++) {
      months.push({ monthNumber: i, year: baseYear });
    }
    // January to March (months 1-3) belong to next year
    for (let i = 1; i <= 3; i++) {
      months.push({ monthNumber: i, year: baseYear + 1 });
    }
    return months;
  }, [baseYear]);

  // Handle month selection (single select)
  const handleMonthSelect = (monthNumber) => {
    const nextMonthNumber = selectedMonth === monthNumber ? null : monthNumber;
    setSelectedMonth(nextMonthNumber);

    const nextMonthValue = nextMonthNumber
      ? `${nextMonthNumber >= 4 ? baseYear : baseYear + 1}-${String(nextMonthNumber).padStart(2, "0")}`
      : null;
    setSelectedMonthValue(nextMonthValue);

    if (onMonthChange) {
      onMonthChange(nextMonthValue);
    }
  };

  // Reset month when financial year changes
  useEffect(() => {
    setSelectedMonth(null);
    setSelectedMonthValue(null);
  }, [selectedFinancialYearId]);

  // Total count from API (same on each row) or fallback to length
  const totalCount = useMemo(() => {
    if (!schools || schools.length === 0) return 0;
    const first = schools[0];
    return typeof first.total !== "undefined" ? first.total : schools.length;
  }, [schools]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          maxHeight: "96vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SchoolIcon sx={{ color: "primary.main" }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
          </Box>
          {totalCount > 0 && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Total: {totalCount}
              {internalSelected.length > 0 && ` • Selected: ${internalSelected.length}`}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Button
            onClick={handleClearAll}
            size="small"
            disabled={internalSelected.length === 0}
            sx={{
              textTransform: "none",
            }}
          >
            Clear All
          </Button>

          <Button
            variant="outlined"
            size="small"
            startIcon={<CalendarIcon />}
            onClick={handleFinancialYearClick}
            disabled={loadingFinancialYears}
            sx={{
              borderRadius: 2,
              textTransform: "none",
            }}
          >
            {(() => {
              if (loadingFinancialYears) return "Financial Year: Loading...";
              if (!selectedFinancialYearId) return "Financial Year: Select";

              const fy = financialYears.find((item) => {
                const id =
                  item.financial_year_id ||
                  item.year_id ||
                  item.id ||
                  item.year;
                // Compare IDs as-is (could be string or number)
                return String(id) === String(selectedFinancialYearId);
              });

              const label = fy
                ? fy.financial_year_name ||
                  fy.name ||
                  fy.label ||
                  String(
                    fy.financial_year_id ||
                      fy.year_id ||
                      fy.id ||
                      fy.year ||
                      selectedFinancialYearId
                  )
                : String(selectedFinancialYearId);

              return `Financial Year: ${label}`;
            })()}
          </Button>

          <Button
            variant="contained"
            size="small"
            onClick={handleDone}
            sx={{
              borderRadius: 2,
              px: 2.5,
              textTransform: "none",
            }}
          >
            Done ({internalSelected.length})
          </Button>

          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "text.secondary",
              "&:hover": { backgroundColor: alpha(theme.palette.error.main, 0.1) },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>

        {/* Financial Year Menu */}
        <Menu
          anchorEl={financialYearAnchorEl}
          open={Boolean(financialYearAnchorEl)}
          onClose={handleFinancialYearClose}
          PaperProps={{
            sx: {
              borderRadius: 2,
              mt: 1,
            },
          }}
        >
          {loadingFinancialYears && (
            <MenuItem disabled>Loading...</MenuItem>
          )}
          {!loadingFinancialYears &&
            financialYears &&
            financialYears.length > 0 &&
            financialYears.map((fy, index) => {
              const id =
                fy.financial_year_id ||
                fy.year_id ||
                fy.id ||
                fy.year;
              const label =
                fy.financial_year_name ||
                fy.name ||
                fy.label ||
                String(id || "");

              if (!id && !label) return null;

              return (
                <MenuItem
                  key={id || index}
                  onClick={() => handleFinancialYearSelect(id)}
                  selected={String(id) === String(selectedFinancialYearId)}
                >
                  {label}
                </MenuItem>
              );
            })}
        </Menu>

        {/* Month Selection Grid - Compact Modern Design */}
        <Box sx={{ mb: 3, mt: '5px' }}>
          <Grid container spacing={1}>
            {getFinancialYearMonths.map(({ monthNumber, year }) => {
              const isSelected = selectedMonth === monthNumber;
              const brandColor = theme.palette.primary.main;
              const blueColor = theme.palette.primary.main;
              
              return (
                <Grid item xs={1} key={`${monthNumber}-${year}`}>
                  <Box
                    sx={{
                      cursor: "pointer",
                      border: isSelected
                        ? `2px solid ${brandColor}`
                        : `1px solid ${blueColor}`,
                      borderRadius: 1.5,
                      backgroundColor: isSelected
                        ? alpha(brandColor, 0.1)
                        : alpha(blueColor, 0.05),
                      p: 0.75,
                      transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.25,
                      minHeight: 48,
                      position: "relative",
                      "&:hover": {
                        borderColor: isSelected ? brandColor : blueColor,
                        backgroundColor: isSelected
                          ? alpha(brandColor, 0.15)
                          : alpha(blueColor, 0.1),
                        transform: "translateY(-1px)",
                      },
                    }}
                    onClick={() => handleMonthSelect(monthNumber)}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: isSelected ? 700 : 500,
                        color: isSelected
                          ? brandColor
                          : blueColor,
                        fontSize: "0.7rem",
                        textAlign: "center",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        lineHeight: 1.2,
                      }}
                    >
                      {getMonthName(monthNumber)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.6rem",
                        color: isSelected
                          ? brandColor
                          : alpha(blueColor, 0.7),
                        fontWeight: isSelected ? 600 : 400,
                        textAlign: "center",
                        lineHeight: 1,
                      }}
                    >
                      ({year})
                    </Typography>
                    {isSelected && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 3,
                          right: 3,
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          backgroundColor: brandColor,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: `0 2px 4px ${alpha(brandColor, 0.3)}`,
                        }}
                      >
                        <CheckIcon
                          sx={{
                            fontSize: "0.7rem",
                            color: "white",
                            fontWeight: "bold",
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* School List */}
        <Box
          sx={{
            maxHeight: "70vh",
            overflowY: "auto",
            pr: 1,
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              borderRadius: 1,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: alpha(theme.palette.primary.main, 0.3),
              borderRadius: 1,
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.5),
              },
            },
          }}
        >
          {loadingSchools ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <LinearProgress />
              <Typography variant="body1" sx={{ mt: 2, color: "text.secondary" }}>
                Loading schools...
              </Typography>
            </Box>
          ) : schools.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <SchoolIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" sx={{ color: "text.secondary", mb: 1 }}>
                No schools found
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Try adjusting your search or filter criteria
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={1}>
              {schools.map((school, index) => {
                // Map API fields (snake_case) to component usage
                const schoolId = school.school_id || school.id;
                const schoolCode = school.school_code || school.schoolCode;
                const schoolName = school.school_name || school.name;
                const contactPerson = school.admin_name || school.contactPerson;
                const phone = school.admin_phone || school.phone;
                const status = school.status || "Active";
                const address = school.school_address || school.address;
                const country = school.country;
                const state = school.state;
                const totalStudents = school.total_students;
                const totalStaff = school.total_staff;
                const duration = school.duration;
                const dateAdded = school.date_added || school.dateAdded;
                const lastLogin = school.last_login || school.lastLogin;

                // Use Set for O(1) lookup instead of O(n) some() calls
                const isSelected = selectedIdsSet.has(schoolId);
                
                // Create unique key using school_code (preferred) or school_id with index as fallback
                const uniqueKey = schoolCode || `${schoolId}-${index}`;
                
                return (
                  <Grid item xs={12} key={uniqueKey}>
                    <Card
                      variant="outlined"
                      sx={{
                        cursor: "pointer",
                        borderColor: isSelected
                          ? theme.palette.primary.main
                          : alpha(theme.palette.divider, 0.7),
                        backgroundColor: isSelected
                          ? alpha(theme.palette.primary.main, 0.08)
                          : alpha(theme.palette.background.paper, 0.9),
                        borderRadius: 1.5,
                        boxShadow: isSelected ? 2 : 0,
                        transition: "all 0.18s ease-in-out",
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                          boxShadow: 1,
                          transform: "translateY(-1px)",
                        },
                      }}
                      onClick={() => handleSelectSchool(schoolId)}
                    >
                      <CardContent
                        sx={{
                          py: 1,
                          px: 1.5,
                          "&:last-child": { pb: 1 },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelectSchool(schoolId)}
                            onClick={(e) => e.stopPropagation()}
                            size="medium"
                            sx={{
                              p: 0.75,
                              color: alpha(theme.palette.primary.main, 0.5),
                              transition: "all 0.2s ease-in-out",
                              "&:hover": {
                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                transform: "scale(1.1)",
                              },
                              "&.Mui-checked": {
                                color: theme.palette.primary.main,
                                "&:hover": {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                },
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: "1.5rem",
                                borderRadius: 1,
                                transition: "all 0.2s ease-in-out",
                              },
                            }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                  minWidth: 0,
                                  flex: 1,
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 600,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {schoolName}
                                </Typography>
                                {(schoolCode || state) && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {schoolCode && (
                                      <Typography
                                        variant="caption"
                                        sx={{ color: "text.secondary" }}
                                      >
                                        ({schoolCode}
                                      </Typography>
                                    )}
                                    {state && (
                                      <Typography
                                        variant="caption"
                                        sx={{ color: "text.secondary" }}
                                      >
                                        {schoolCode ? ` • ${state})` : `(${state})`}
                                      </Typography>
                                    )}
                                    {schoolCode && !state && (
                                      <Typography
                                        variant="caption"
                                        sx={{ color: "text.secondary" }}
                                      >
                                        )
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                              </Box>
                              <Chip
                                label={status}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: "0.7rem",
                                  fontWeight: 500,
                                  borderRadius: 1,
                                }}
                              />
                            </Box>
                            {/* Row 2: contact person + phone */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                mt: 0.25,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  flex: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {contactPerson}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  flexShrink: 0,
                                }}
                              >
                                <PhoneIcon
                                  fontSize="small"
                                  sx={{ fontSize: "0.8rem", color: "text.secondary" }}
                                />
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                  {phone}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Row 3: address / location */}
                            {(address || state || country) && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  mt: 0.25,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {address}
                                {(state || country) &&
                                  ` ${address ? "• " : ""}${state || ""}${
                                    state && country ? ", " : ""
                                  }${country || ""}`}
                              </Typography>
                            )}

                            {/* Row 4: meta (dates + students / staff / duration) */}
                            {(dateAdded || lastLogin || totalStudents || totalStaff || duration) && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: 0.75,
                                  mt: 0.25,
                                }}
                              >
                                {dateAdded && (
                                  <Chip
                                    label={`Added on: ${dateAdded}`}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontSize: "0.7rem",
                                      borderRadius: 1.5,
                                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                      color: theme.palette.primary.dark,
                                      fontWeight: 500,
                                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    }}
                                  />
                                )}
                                {lastLogin && (
                                  <Chip
                                    label={`Last login on: ${lastLogin}`}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontSize: "0.7rem",
                                      borderRadius: 1.5,
                                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                      color: theme.palette.primary.main,
                                      fontWeight: 600,
                                      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                    }}
                                  />
                                )}
                                {typeof totalStudents !== "undefined" && (
                                  <Chip
                                    label={`Student: ${totalStudents}`}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontSize: "0.7rem",
                                      borderRadius: 1.5,
                                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                      color: theme.palette.primary.main,
                                      fontWeight: 500,
                                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    }}
                                  />
                                )}
                                {typeof totalStaff !== "undefined" && (
                                  <Chip
                                    label={`Staff: ${totalStaff}`}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontSize: "0.7rem",
                                      borderRadius: 1.5,
                                      backgroundColor: alpha("#8b5cf6", 0.1),
                                      color: "#6b21a8",
                                      fontWeight: 500,
                                      border: `1px solid ${alpha("#8b5cf6", 0.2)}`,
                                    }}
                                  />
                                )}
                                {duration && (
                                  <Chip
                                    label={duration}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontSize: "0.7rem",
                                      borderRadius: 1.5,
                                      backgroundColor: alpha("#f59e0b", 0.1),
                                      color: "#92400e",
                                      fontWeight: 500,
                                      border: `1px solid ${alpha("#f59e0b", 0.2)}`,
                                    }}
                                  />
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          justifyContent: "flex-end",
        }}
      >
        {/* Footer left intentionally minimal as actions are in header */}
      </DialogActions>
    </Dialog>
  );
};

export default MdlSelectMultipleSchool;

